/**
 * Kontakt-Detail-Route (W6, 2026-07-06).
 *
 * Route:  /admin/adressbuch/contacts/:id
 * Analog zu AdressbuchPersonAkte (Mitarbeiter). Bringt den Kunden-Kontext
 * auf eine eigene Route mit Sticky-Header-Bearbeiten-Button + Tabs
 * (Stammdaten/Aufgaben/Berichte/Akte/Projekte). Bearbeiten passiert inline,
 * kein Modal — dadurch bleibt der Kontext erhalten wenn der Nutzer in einen
 * Bericht oder eine Aufgabe abtaucht.
 *
 * Fuer Ansprechpartner (typus=person mit firma_id) fallen die Kunden-Tabs
 * weg, es bleibt nur Stammdaten mit Link zur Firma.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, X, ListTodo, FileText, FolderTree, LayoutGrid, Building2, User as UserIcon, RefreshCcw, UserPlus, ArrowRightLeft, Eye, EyeOff } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';
import { BackLink, ConfirmModal, EditBar, ParentLinks, SaveFeedback, UnsavedChangesModal, param, parentRef, useBack, useDialogs, useEditSession, useListState, usePageLabel } from '@onvice/portal-ui';

import AkteStrukturViewer from '../../components/AkteStrukturViewer';
import { CustomerProjectsTab } from '../../components/CustomerAkteEmbeddedPanel';
import { useFederatedModule } from '../../lib/useFederatedModule';
import type { ComponentType } from 'react';

// stammdaten/bauberichteV2 dynamisch — statische Imports wuerden den
// Federation-Bootstrap-Preload zwingen und einen einzelnen Container-Ausfall
// zum globalen Portal-Boot-Kill machen. Regel:
// [[feedback_federation_no_static_imports]].
type CustomerSummaryData = Record<string, unknown> & { id: string };
type CustomerTaskRow = Record<string, unknown> & { id: string };
type CustomerReportRow = Record<string, unknown> & { id: string };
type StammdatenPublic = {
  CustomerSummary: ComponentType<{ customer: CustomerSummaryData }>;
};
type BauberichteV2Public = {
  CustomerTasksList: ComponentType<{ tasks: CustomerTaskRow[] }>;
  CustomerReportsList: ComponentType<{ reports: CustomerReportRow[] }>;
};

type Tab = 'stammdaten' | 'aufgaben' | 'berichte' | 'akte' | 'projekte';
const TABS: readonly Tab[] = ['stammdaten', 'aufgaben', 'berichte', 'akte', 'projekte'];
// Dokument-Navigation 1.41 (Jörg 2026-09-29, R2): der Reiter steht in der Adresse
// (ersetzend), damit „Zurück" aus einer Aufgabe wieder im Reiter „Aufgaben" landet.
const TAB_SCHEMA = { tab: param.oneOf(TABS, 'stammdaten') };

function ModulOffline({ modul }: { modul: string }) {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
      Modul <b>{modul}</b> ist gerade nicht erreichbar. Sobald es antwortet, erscheint dieser Inhalt automatisch.
    </div>
  );
}

interface ContactRow {
  id: string;
  typus: 'firma' | 'person';
  firma_id: string | null;
  firma_name: string | null;
  kuerzel: string | null;
  ust_id: string | null;
  hr_eintrag: string | null;
  bank_iban: string | null;
  zahlungsziel_tage: number | null;
  anrede: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  position: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  telefon: string | null;
  mobil: string | null;
  email: string | null;
  notizen: string | null;
  customer_number: string | null;
  kategorien: string[];
}

interface CustomerDetailsRow {
  contact_id: string;
  customer_number: string | null;
  kostenstellen: string[];
  kostenstellen_inaktiv?: string[];
  lat: number | null;
  lng: number | null;
  gps_confirmed_at: string | null;
  /** Timesheet 2026-09-27: Fahrtzeit je einfache Strecke (Minuten). */
  fahrtzeit_minuten?: number | null;
}

interface OverviewResponse {
  customer: CustomerSummaryData;
  counts: { tasks: number; reports: number; akten_docs: number; projects?: number };
  tasks: CustomerTaskRow[];
  reports: CustomerReportRow[];
}

interface Form {
  // Firma-Stammdaten
  firma_name: string;
  kuerzel: string;
  ust_id: string;
  hr_eintrag: string;
  bank_iban: string;
  zahlungsziel_tage: string; // im UI als String (leere Eingabe = null)
  // Person-Stammdaten
  anrede: string;
  titel: string;
  vorname: string;
  nachname: string;
  position: string;
  // Adresse
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land: string;
  // Kommunikation
  telefon: string;
  mobil: string;
  email: string;
  // Sonstiges
  notizen: string;
  // Kunden-spezifisch (via customer-details Endpoint)
  customer_number: string;
  kostenstellen: string[];
  // Joerg 2026-08-07: Delta-Liste inaktiver KS. Alte KS bleiben in der
  // Voll-Liste sichtbar (Historie), aber werden bei Neuanlage von Aufgaben/
  // Berichten aus dem Picker gefiltert. Ohne UI hier wurde v2.4.1 nie live
  // sichtbar — der KundenStammEditModal war Dead Code.
  kostenstellen_inaktiv: string[];
  lat: string;
  lng: string;
  // Jörg 2026-09-28 (Fund 6, „ich sehe das Feld nicht"): Fahrtzeit gehört HIER in
  // die Akte — KundenStammEditModal ist toter Code (s. o.), dort war es zuerst gelandet.
  fahrtzeit_minuten: string;
}

const emptyForm: Form = {
  firma_name: '', kuerzel: '', ust_id: '', hr_eintrag: '', bank_iban: '', zahlungsziel_tage: '',
  anrede: '', titel: '', vorname: '', nachname: '', position: '',
  strasse: '', hausnummer: '', plz: '', ort: '', land: '',
  telefon: '', mobil: '', email: '',
  notizen: '',
  customer_number: '',
  kostenstellen: [], kostenstellen_inaktiv: [], lat: '', lng: '', fahrtzeit_minuten: '',
};

/** Ausgangsstand der Arbeitskopie aus Kontakt + Kundendetails (eine Stelle für Laden und Abbrechen). */
function formFrom(cr: ContactRow, d: CustomerDetailsRow | null): Form {
  return {
    firma_name: cr.firma_name ?? '',
    kuerzel: cr.kuerzel ?? '',
    ust_id: cr.ust_id ?? '',
    hr_eintrag: cr.hr_eintrag ?? '',
    bank_iban: cr.bank_iban ?? '',
    zahlungsziel_tage: cr.zahlungsziel_tage != null ? String(cr.zahlungsziel_tage) : '',
    anrede: cr.anrede ?? '',
    titel: cr.titel ?? '',
    vorname: cr.vorname ?? '',
    nachname: cr.nachname ?? '',
    position: cr.position ?? '',
    strasse: cr.strasse ?? '',
    hausnummer: cr.hausnummer ?? '',
    plz: cr.plz ?? '',
    ort: cr.ort ?? '',
    land: cr.land ?? '',
    telefon: cr.telefon ?? '',
    mobil: cr.mobil ?? '',
    email: cr.email ?? '',
    notizen: cr.notizen ?? '',
    customer_number: d?.customer_number ?? cr.customer_number ?? '',
    kostenstellen: d?.kostenstellen ?? [],
    kostenstellen_inaktiv: d?.kostenstellen_inaktiv ?? [],
    lat: d?.lat != null ? String(d.lat) : '',
    lng: d?.lng != null ? String(d.lng) : '',
    fahrtzeit_minuten: d?.fahrtzeit_minuten != null ? String(d.fahrtzeit_minuten) : '',
  };
}

function contactDisplayName(contact: ContactRow): string {
  return contact.typus === 'firma'
    ? contact.firma_name ?? '(unbenannte Firma)'
    : `${contact.anrede ? contact.anrede + ' ' : ''}${contact.titel ? contact.titel + ' ' : ''}${contact.vorname ?? ''} ${contact.nachname ?? ''}`.trim();
}

export default function AdressbuchContactAkte() {
  const { id: contactId } = useParams<{ id: string }>();
  const { isAdmin, hasPermission } = useAuth();
  const dlg = useDialogs();
  const [searchParams, setSearchParams] = useSearchParams();
  // Dokument-Navigation (Jörg 2026-09-29): Zurück geht im Verlauf zurück (R2); Rückfall
  // bei Direktaufruf ist die Kontaktliste. `?edit=1` ist nur Tiefenlink (R3).
  const back = useBack({ fallback: '/admin/adressbuch?tab=contacts', fallbackLabel: 'Adressbuch' });
  const initialEdit = useRef(searchParams.get('edit') === '1' || searchParams.get('edit') === 'true').current;
  useEffect(() => {
    if (!searchParams.has('edit')) return;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('edit'); return next; }, { replace: true, preventScrollReset: true });
  }, [searchParams, setSearchParams]);
  const [{ tab }, setTabState] = useListState(TAB_SCHEMA);
  const setTab = (t: Tab) => setTabState({ tab: t });
  const stammdaten = useFederatedModule<StammdatenPublic>('stammdaten/public');
  const bv2 = useFederatedModule<BauberichteV2Public>('bauberichteV2/public');
  const CustomerSummary = stammdaten?.CustomerSummary ?? null;
  const CustomerTasksList = bv2?.CustomerTasksList ?? null;
  const CustomerReportsList = bv2?.CustomerReportsList ?? null;
  // SD-2026-00021 v3 (Onvice, 2026-08-01): Permission-basiert (adressbuch.write) statt Rollen.
  // customers.create bleibt als Legacy-Fallback fuer alte Rollen-Definitionen.
  const canEdit = isAdmin || hasPermission('adressbuch.write') || hasPermission('customers.create');

  const [contact, setContact] = useState<ContactRow | null>(null);
  const [details, setDetails] = useState<CustomerDetailsRow | null>(null);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ksInput, setKsInput] = useState('');
  // Fund 9 (Jörg 2026-09-28): Koordinate aus der Anschrift über die lokale
  // Adress-Datenbank (stammdaten /customers/geocode via /adressbuch/contacts/geocode).
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoInfo, setGeoInfo] = useState<string | null>(null);
  // W5 2026-08-04: Typus-Switch Firma ↔ Einzelperson (mit Bestaetigung).
  const [switchingTypus, setSwitchingTypus] = useState(false);
  const [switchTypusOpen, setSwitchTypusOpen] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true); setError(null);
    try {
      const [c, d, o] = await Promise.all([
        api.get<{ success: boolean; data: ContactRow }>(`/adressbuch/contacts/${contactId}`),
        api.get<{ success: boolean; data: CustomerDetailsRow }>(`/adressbuch/contacts/${contactId}/customer-details`).catch(() => null),
        api.get<{ success: boolean; data: OverviewResponse }>(`/portal/contacts/${contactId}/overview`).catch(() => null),
      ]);
      const cr = c.data;
      setContact(cr);
      setDetails(d?.data ?? null);
      setOverview(o?.data ?? null);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { void load(); }, [load]);

  const isKunde = useMemo(() => (contact?.kategorien ?? []).includes('kunde'), [contact]);
  const isAnsprechpartner = contact?.typus === 'person' && !!contact.firma_id;

  // Lese-/Bearbeitungsmodus mit Arbeitskopie (R3–R5): „Speichern" → Lesemodus, Dokument bleibt
  // offen (E1); „Abbrechen" → dorthin, wo der Anwender herkam, mit Rückfrage (E2).
  const baseline = useMemo(() => (contact ? formFrom(contact, details) : null), [contact, details]);
  const session = useEditSession<Form>({
    id: `contact:${contactId ?? ''}`,
    label: 'Kontakt',
    baseline,
    initialMode: initialEdit ? 'edit' : 'read',
    canEdit,
    validate: (f) => {
      const issues: string[] = [];
      if (contact?.typus === 'firma' && !f.firma_name.trim()) issues.push('Firmenname');
      if (contact?.typus === 'person' && !f.nachname.trim()) issues.push('Nachname');
      const fzRaw = f.fahrtzeit_minuten.trim();
      if (isKunde && fzRaw) {
        const fz = Number(fzRaw);
        if (!Number.isInteger(fz) || fz < 0 || fz > 1440) issues.push('Fahrtzeit: ganze Minuten zwischen 0 und 1440');
      }
      return issues;
    },
    onSave: (value) => saveForm(value),
    onLeave: back.go,
  });
  const form = session.value ?? emptyForm;
  const editing = session.editing;
  const setField = session.setField;
  usePageLabel(contact ? contactDisplayName(contact) : null, 'document');

  interface GeocodeHit { lat: number; lng: number; displayName: string; source: 'exact' | 'fuzzy'; score?: number }

  /** Anschrift → Koordinate. Liefert den Treffer oder null; wirft bei 503 (Adress-DB fehlt) einen sprechenden Fehler. */
  async function geocodeAddress(f: Form): Promise<GeocodeHit | null> {
    const street = [f.strasse.trim(), f.hausnummer.trim()].filter(Boolean).join(' ');
    const place = [f.plz.trim(), f.ort.trim()].filter(Boolean).join(' ');
    const q = [street, place].filter(Boolean).join(', ');
    if (!street || !place) return null;
    try {
      const r = await api.get<{ success: boolean; data: GeocodeHit | null }>(`/adressbuch/contacts/geocode?q=${encodeURIComponent(q)}`);
      return r.data ?? null;
    } catch (e: unknown) {
      const msg = (e as Error).message;
      if (/503|nicht verf/i.test(msg)) throw new Error('Adress-Datenbank auf dieser Instanz nicht verfügbar');
      throw e;
    }
  }

  async function geocodeFromAddress() {
    setGeoBusy(true); setGeoInfo(null);
    try {
      if (!(form.strasse.trim() && (form.plz.trim() || form.ort.trim()))) {
        setGeoInfo('Bitte zuerst Straße und PLZ/Ort in der Anschrift pflegen.'); return;
      }
      const hit = await geocodeAddress(form);
      if (!hit) { setGeoInfo('Adresse nicht gefunden — Schreibweise prüfen oder Koordinate von Hand eintragen.'); return; }
      setField('lat', hit.lat.toFixed(6));
      setField('lng', hit.lng.toFixed(6));
      setGeoInfo(hit.source === 'exact'
        ? `Gefunden: ${hit.displayName}`
        : `Ähnliche Adresse gefunden: ${hit.displayName} — bitte prüfen`);
    } catch (e: unknown) {
      setGeoInfo((e as Error).message);
    } finally {
      setGeoBusy(false);
    }
  }

  function addKs() {
    const v = ksInput.trim();
    if (!v || form.kostenstellen.includes(v)) { setKsInput(''); return; }
    setField('kostenstellen', [...form.kostenstellen, v]);
    setKsInput('');
  }
  function removeKs(v: string) {
    setField('kostenstellen', form.kostenstellen.filter((k) => k !== v));
    // Auch aus der Inaktiv-Delta-Liste raeumen, sonst bleiben Waisen.
    setField('kostenstellen_inaktiv', form.kostenstellen_inaktiv.filter((k) => k !== v));
  }
  function isKsInaktiv(v: string): boolean {
    return form.kostenstellen_inaktiv.includes(v);
  }
  function toggleKsAktiv(v: string) {
    setField(
      'kostenstellen_inaktiv',
      isKsInaktiv(v)
        ? form.kostenstellen_inaktiv.filter((k) => k !== v)
        : [...form.kostenstellen_inaktiv, v],
    );
  }

  /** Schreibt die Arbeitskopie; wirft bei Fehlern (die Sitzung zeigt sie als Meldung). */
  async function saveForm(form: Form): Promise<void> {
    if (!contactId || !contact) return;
    {
      const isFirma = contact.typus === 'firma';
      const isPerson = contact.typus === 'person';
      const zahlungsziel = form.zahlungsziel_tage.trim();
      // Firma-Felder nur bei Firma senden, Person-Felder nur bei Person —
      // das Backend haelt die Felder sonst konsistent leer (analog Neu-Anlegen).
      const payload: Record<string, unknown> = {
        firma_name: isFirma ? (form.firma_name.trim() || null) : null,
        kuerzel: isFirma ? (form.kuerzel.trim() || null) : null,
        ust_id: isFirma ? (form.ust_id.trim() || null) : null,
        hr_eintrag: isFirma ? (form.hr_eintrag.trim() || null) : null,
        bank_iban: isFirma ? (form.bank_iban.trim() || null) : null,
        zahlungsziel_tage: isFirma && zahlungsziel ? Number(zahlungsziel) : null,
        anrede: isPerson ? (form.anrede.trim() || null) : null,
        titel: isPerson ? (form.titel.trim() || null) : null,
        vorname: isPerson ? (form.vorname.trim() || null) : null,
        nachname: isPerson ? (form.nachname.trim() || null) : null,
        position: isPerson ? (form.position.trim() || null) : null,
        strasse: form.strasse.trim() || null,
        hausnummer: form.hausnummer.trim() || null,
        plz: form.plz.trim() || null,
        ort: form.ort.trim() || null,
        land: form.land.trim() || null,
        telefon: form.telefon.trim() || null,
        mobil: form.mobil.trim() || null,
        email: form.email.trim() || null,
        notizen: form.notizen.trim() || null,
      };
      await api.put(`/adressbuch/contacts/${contactId}`, payload);
      if (isKunde) {
        let latNum = form.lat.trim() ? Number(form.lat.trim().replace(',', '.')) : null;
        let lngNum = form.lng.trim() ? Number(form.lng.trim().replace(',', '.')) : null;
        // Fund 9: Kunde mit Anschrift, aber ohne Koordinate → beim Speichern still
        // nachschlagen (nur exakter Treffer; unscharfe Treffer nur per Knopf, damit
        // nichts Falsches unbemerkt gespeichert wird). Fehler hier brechen das
        // Speichern nicht ab.
        if (latNum == null && lngNum == null) {
          try {
            const hit = await geocodeAddress(form);
            if (hit && hit.source === 'exact') { latNum = hit.lat; lngNum = hit.lng; }
          } catch { /* Adress-DB fehlt → ohne Koordinate speichern */ }
        }
        const gpsChanged = latNum !== (details?.lat ?? null) || lngNum !== (details?.lng ?? null);
        const fzRaw = form.fahrtzeit_minuten.trim();
        const fahrtzeit = fzRaw ? Number(fzRaw) : null;
        await api.put(`/adressbuch/contacts/${contactId}/customer-details`, {
          customer_number: form.customer_number.trim() || null,
          kostenstellen: form.kostenstellen,
          kostenstellen_inaktiv: form.kostenstellen_inaktiv,
          lat: latNum, lng: lngNum,
          gpsConfirmed: !!(gpsChanged && latNum != null && lngNum != null),
          fahrtzeit_minuten: fahrtzeit,
        });
      }
      await load();
    }
  }

  // W5 2026-08-04: Typus zwischen firma/person umschalten. Backend clear't
  // die jeweils inaktiven Felder (Vor-/Nachname vs. firma_name) sobald sie
  // nicht explizit gesetzt sind. Wir muessen aber die neuen Pflichtfelder
  // liefern (Constraint contacts_typus_firma_fields: firma_name NOT NULL;
  // contacts_typus_person_fields: nachname NOT NULL). Fuellen aus dem
  // bestehenden Namensfeld, damit die Zeile nicht leer verpuffet — User
  // kann danach in Ruhe editieren. firma_id wird immer geleert.
  async function switchTypus() {
    if (!contactId || !contact) return;
    setSwitchingTypus(true);
    try {
      const newTypus = contact.typus === 'firma' ? 'person' : 'firma';
      const payload: Record<string, unknown> = { typus: newTypus, firma_id: null };
      if (newTypus === 'firma') {
        const combined = `${contact.vorname ?? ''} ${contact.nachname ?? ''}`.trim();
        payload.firma_name = combined || '(unbenannt)';
      } else {
        payload.nachname = (contact.firma_name ?? '').trim() || '(unbenannt)';
      }
      await api.put(`/adressbuch/contacts/${contactId}`, payload);
      setSwitchTypusOpen(false);
      await load();
    } catch (e: unknown) {
      await dlg.error('Umschalten fehlgeschlagen', e);
    } finally {
      setSwitchingTypus(false);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-400 gap-2"><Loader2 className="animate-spin" size={16} /> Lädt Kontakt…</div>;
  }
  if (error || !contact) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <BackLink variant="text" fallback="/admin/adressbuch?tab=contacts" fallbackLabel="Adressbuch" className="mb-4" />
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">{error ?? 'Kontakt nicht gefunden'}</div>
      </div>
    );
  }

  const displayName = contactDisplayName(contact);

  const tabs: Tab[] = ['stammdaten'];
  if (isKunde) tabs.push('aufgaben', 'berichte', 'akte', 'projekte');

  // Eltern im Kopf (R1): Ansprechpartner gehören zu ihrer Firma. Kunden und Firmen sind
  // selbst die Wurzel — sie haben keine Eltern im Adressbuch.
  const parents = isAnsprechpartner ? [parentRef.firma(contact.firma_id, contact.firma_name)] : [];

  return (
    <div className="min-h-screen" data-test-anchor="contact-page" data-mode={session.mode}>
      {/* Sticky Header */}
      <div className="sticky top-14 z-20 bg-card/95 backdrop-blur border-b border-default">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <BackLink fallback="/admin/adressbuch?tab=contacts" fallbackLabel="Adressbuch" />
          <div className="text-2xl flex-shrink-0">{contact.typus === 'firma' ? '🏢' : '👤'}</div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-foreground truncate">{displayName}</h1>
            <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
              {isKunde && form.customer_number && <span className="font-mono">{form.customer_number}</span>}
              {contact.kategorien.map((k) => <span key={k} className="text-brand-400">· {k}</span>)}
            </div>
            {parents.length > 0 && <ParentLinks items={parents} className="mt-1" />}
          </div>
          {session.dirty && <span data-state="dirty" className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 whitespace-nowrap">ungespeichert</span>}
          {canEdit && !editing && contact.typus === 'firma' && (
            <Link
              to={`/admin/adressbuch/contacts/new?typus=person&kategorie=ansprechpartner&firma_id=${contact.id}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm"
              title="Ansprechpartner fuer diese Firma anlegen"
            >
              <UserPlus size={14} /> Ansprechpartner
            </Link>
          )}
          {/* 2026-08-31 (Joerg): auch Ansprechpartner duerfen umgeschaltet werden —
              das Modal warnt, dass die Firma-Zuordnung dabei geloest wird
              (Backend leert firma_id beim Typus-Wechsel ohnehin). */}
          {canEdit && !editing && (
            <button
              onClick={() => setSwitchTypusOpen(true)}
              disabled={switchingTypus}
              title={contact.typus === 'firma' ? 'Zu Einzelperson umschalten' : 'Zu Firma umschalten'}
              className="inline-flex items-center gap-1 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm disabled:opacity-50"
            >
              <ArrowRightLeft size={14} />
              {contact.typus === 'firma' ? 'Zu Person' : 'Zu Firma'}
            </button>
          )}
          <EditBar
            editing={editing}
            dirty={session.dirty}
            saving={session.saving}
            canEdit={canEdit}
            onEdit={session.startEdit}
            onSave={() => { void session.save(); }}
            onCancel={session.cancel}
          />
        </div>

        {/* Tab-Bar */}
        {tabs.length > 1 && (
          <nav className="max-w-6xl mx-auto px-4 border-t border-slate-800/60">
            <ul className="flex flex-wrap gap-1 -mb-px">
              {tabs.map((t) => {
                const label = t === 'stammdaten' ? 'Stammdaten'
                  : t === 'aufgaben' ? `Aufgaben${overview ? ' (' + overview.counts.tasks + ')' : ''}`
                  : t === 'berichte' ? `Berichte${overview ? ' (' + overview.counts.reports + ')' : ''}`
                  : t === 'akte' ? `Akte${overview ? ' (' + overview.counts.akten_docs + ')' : ''}`
                  : `Projekte${overview?.counts.projects != null ? ' (' + overview.counts.projects + ')' : ''}`;
                const active = tab === t;
                return (
                  <li key={t}>
                    <button
                      type="button"
                      onClick={() => setTab(t)}
                      className={`px-3 py-2 text-sm border-b-2 transition-colors ${
                        active ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {tab === 'stammdaten' && (
          <div className="space-y-6">
            {/* Basis-Felder */}
            <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4">
              <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider flex items-center gap-2">
                {contact.typus === 'firma' ? <Building2 size={14} /> : <UserIcon size={14} />}
                {contact.typus === 'firma' ? 'Firma' : 'Person'}
              </h2>
              {contact.typus === 'firma' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FieldOrText label="Firmenname" value={form.firma_name} editing={editing} onChange={(v) => setField('firma_name', v)} className="sm:col-span-2" />
                  {isKunde && <FieldOrText label="Kundennummer" value={form.customer_number} editing={editing} onChange={(v) => setField('customer_number', v)} monospace placeholder="K-00001 (auto)" />}
                  <FieldOrText label="Kuerzel" value={form.kuerzel} editing={editing} onChange={(v) => setField('kuerzel', v)} monospace placeholder="z.B. MEIER" />
                  <FieldOrText label="USt-IdNr." value={form.ust_id} editing={editing} onChange={(v) => setField('ust_id', v)} monospace placeholder="DE123456789" />
                  <FieldOrText label="HR-Eintrag" value={form.hr_eintrag} editing={editing} onChange={(v) => setField('hr_eintrag', v)} placeholder="HRB 12345 Berlin" />
                  <FieldOrText label="Zahlungsziel (Tage)" value={form.zahlungsziel_tage} editing={editing} onChange={(v) => setField('zahlungsziel_tage', v.replace(/[^0-9]/g, ''))} placeholder="14" />
                  <FieldOrText label="IBAN" value={form.bank_iban} editing={editing} onChange={(v) => setField('bank_iban', v)} monospace placeholder="DE89 3704 …" className="sm:col-span-3" />
                </div>
              )}
              {contact.typus === 'person' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FieldOrText label="Anrede" value={form.anrede} editing={editing} onChange={(v) => setField('anrede', v)} placeholder="Herr / Frau" />
                  <FieldOrText label="Titel" value={form.titel} editing={editing} onChange={(v) => setField('titel', v)} placeholder="Dr., Prof." />
                  <div />
                  <FieldOrText label="Vorname" value={form.vorname} editing={editing} onChange={(v) => setField('vorname', v)} />
                  <FieldOrText label="Nachname" value={form.nachname} editing={editing} onChange={(v) => setField('nachname', v)} />
                  <FieldOrText label="Position" value={form.position} editing={editing} onChange={(v) => setField('position', v)} placeholder="z.B. Einkauf" />
                  {contact.firma_id && (
                    <div className="sm:col-span-3">
                      <div className="text-xs text-slate-500 mb-1">Firma</div>
                      <Link to={`/admin/adressbuch/contacts/${contact.firma_id}`} className="text-brand-300 hover:text-brand-200 underline">
                        🏢 zur Firma
                      </Link>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-3">
                  <FieldOrText label="Strasse" value={form.strasse} editing={editing} onChange={(v) => setField('strasse', v)} />
                </div>
                <FieldOrText label="Hausnummer" value={form.hausnummer} editing={editing} onChange={(v) => setField('hausnummer', v)} />
                <FieldOrText label="PLZ" value={form.plz} editing={editing} onChange={(v) => setField('plz', v)} />
                <div className="sm:col-span-2">
                  <FieldOrText label="Ort" value={form.ort} editing={editing} onChange={(v) => setField('ort', v)} />
                </div>
                <FieldOrText label="Land" value={form.land} editing={editing} onChange={(v) => setField('land', v)} placeholder="Deutschland" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FieldOrText label="Telefon" value={form.telefon} editing={editing} onChange={(v) => setField('telefon', v)} type="tel" />
                <FieldOrText label="Mobil" value={form.mobil} editing={editing} onChange={(v) => setField('mobil', v)} type="tel" />
                <FieldOrText label="E-Mail" value={form.email} editing={editing} onChange={(v) => setField('email', v)} type="email" />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Notizen</div>
                {editing ? (
                  <textarea
                    value={form.notizen}
                    onChange={(e) => setField('notizen', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                    placeholder="Interne Notizen zum Kontakt…"
                  />
                ) : (
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{form.notizen || <span className="italic text-slate-500">—</span>}</div>
                )}
              </div>
            </section>

            {isKunde && (
              <>
                {/* GPS + Kostenstellen — nur bei Kunde */}
                <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-3">
                  <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">GPS, Fahrtzeit + Kostenstellen</h2>
                  {editing ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FieldOrText label="Breite (lat)" value={form.lat} editing onChange={(v) => setField('lat', v)} placeholder="52.520008" />
                        <FieldOrText label="Länge (lng)" value={form.lng} editing onChange={(v) => setField('lng', v)} placeholder="13.404954" />
                      </div>
                      <div data-test-anchor="gps" className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => void geocodeFromAddress()} disabled={geoBusy || !form.strasse.trim()}
                                className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm disabled:opacity-40"
                                title="Koordinate aus der Anschrift ermitteln (lokale Adress-Datenbank)">
                          {geoBusy ? 'Suche …' : 'Aus Adresse'}
                        </button>
                        <span className="text-xs text-slate-500">
                          {geoInfo ?? 'Ohne Koordinate wird beim Speichern automatisch aus der Anschrift nachgeschlagen.'}
                        </span>
                      </div>
                      <div data-test-anchor="fahrtzeit">
                        <label className="block text-xs text-slate-500 mb-1">
                          Fahrtzeit zur Baustelle (Minuten, einfache Strecke)
                          <span className="ml-2 text-[10px] text-slate-500">Vorbelegung für „+ Fahrt" im Timesheet des Berichts</span>
                        </label>
                        <input type="number" min={0} max={1440} step={5} inputMode="numeric" placeholder="z. B. 25"
                               value={form.fahrtzeit_minuten} onChange={(e) => setField('fahrtzeit_minuten', e.target.value)}
                               className="w-full sm:w-48 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Kostenstellen</div>
                        {/* Joerg 2026-08-07: Auge-Toggle pro KS. Aktiv (Auge auf) = im Picker
                             bei Neuanlage waehlbar. Inaktiv (Auge zu) = ausgeblendet + durchgestrichen,
                             bleibt aber am Kunden fuer Historie. X entfernt aus beiden Listen. */}
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {form.kostenstellen.length === 0 && <span className="text-xs text-slate-500 italic">keine</span>}
                          {form.kostenstellen.map((ks) => {
                            const inaktiv = isKsInaktiv(ks);
                            return (
                              <span
                                key={ks}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${
                                  inaktiv
                                    ? 'bg-slate-900/60 border-slate-700 text-slate-500 line-through'
                                    : 'bg-slate-800 border-slate-700 text-slate-200'
                                }`}
                              >
                                {ks}
                                {inaktiv && <span className="not-italic no-underline text-slate-500">(inaktiv)</span>}
                                <button
                                  type="button"
                                  onClick={() => toggleKsAktiv(ks)}
                                  title={inaktiv ? 'Wieder aktiv setzen' : 'Als inaktiv markieren (aus Picker ausblenden)'}
                                  className={`no-underline ${inaktiv ? 'text-slate-500 hover:text-emerald-400' : 'text-slate-400 hover:text-amber-400'}`}
                                >
                                  {inaktiv ? <EyeOff size={12} /> : <Eye size={12} />}
                                </button>
                                <button type="button" onClick={() => removeKs(ks)} title="KS ganz vom Kunden entfernen" className="no-underline text-slate-500 hover:text-rose-400"><X size={12} /></button>
                              </span>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <input value={ksInput} onChange={(e) => setKsInput(e.target.value)}
                                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKs(); } }}
                                 placeholder="z.B. KS-100"
                                 className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
                          <button type="button" onClick={addKs} disabled={!ksInput.trim()} className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-40 text-sm">+ Hinzufügen</button>
                        </div>
                        <div className="text-xs text-slate-500 mt-1.5">
                          Klick auf das Auge macht eine KS inaktiv — sie bleibt am Kunden sichtbar,
                          wird aber bei neuen Aufgaben/Berichten nicht mehr zur Auswahl angeboten.
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-slate-300 space-y-2">
                      <div><span className="text-slate-500">GPS:</span> {form.lat && form.lng ? <>{form.lat}, {form.lng}</> : <span className="italic text-slate-500">nicht gesetzt</span>}</div>
                      <div data-test-anchor="fahrtzeit">
                        <span className="text-slate-500">Fahrtzeit zur Baustelle:</span>{' '}
                        {form.fahrtzeit_minuten
                          ? <>{form.fahrtzeit_minuten} Min. einfache Strecke ({Number(form.fahrtzeit_minuten) * 2} Min. hin und zurück)</>
                          : <span className="italic text-slate-500">nicht gesetzt</span>}
                      </div>
                      <div>
                        <span className="text-slate-500">Kostenstellen:</span>{' '}
                        {form.kostenstellen.length === 0
                          ? <span className="italic text-slate-500">keine</span>
                          : form.kostenstellen.map((ks) => {
                              const inaktiv = isKsInaktiv(ks);
                              return (
                                <span
                                  key={ks}
                                  className={`inline-block ml-1 px-2 py-0.5 rounded-full border text-xs ${
                                    inaktiv
                                      ? 'bg-slate-900/60 border-slate-700 text-slate-500 line-through'
                                      : 'bg-slate-800 border-slate-700'
                                  }`}
                                >
                                  {ks}
                                  {inaktiv && <span className="ml-1 not-italic no-underline text-slate-500">(inaktiv)</span>}
                                </span>
                              );
                            })}
                      </div>
                    </div>
                  )}
                </section>
              </>
            )}

            {!isKunde && isAnsprechpartner && (
              <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider mb-2">Letzte Kontakte</h2>
                <p className="text-xs text-slate-500 italic">
                  Zeigt künftig die letzten Aufgaben, Berichte und Termine dieses Ansprechpartners.
                  Wird geliefert, sobald diese direkt mit Kontakten verknüpft werden.
                </p>
              </section>
            )}
          </div>
        )}

        {/* `returnTo` entfällt (1.32/1.41): Zurück geht im Verlauf zurück und trifft diesen Reiter. */}
        {tab === 'aufgaben' && overview && (
          CustomerTasksList
            ? <CustomerTasksList tasks={overview.tasks} />
            : <ModulOffline modul="Bauberichte" />
        )}

        {tab === 'berichte' && overview && (
          CustomerReportsList
            ? <CustomerReportsList reports={overview.reports} />
            : <ModulOffline modul="Bauberichte" />
        )}

        {tab === 'akte' && overview && (
          <AkteStrukturViewer source={{ kind: 'customer', customerId: overview.customer.id }} configTyp="kunde" />
        )}

        {/* SD-2026-00022 (Onvice, 2026-07-31): Projekte-Panel eingebettet.
            Vorher stand hier "Projekte-Ansicht folgt" + Link auf Legacy-/kunden/:id,
            das inzwischen ein Redirect zurueck auf /admin/adressbuch ist. */}
        {tab === 'projekte' && overview && (
          <CustomerProjectsTab customerId={overview.customer.id} />
        )}

        {tab === 'stammdaten' && !editing && isKunde && overview && (
          <section className="mt-6 rounded-lg border border-slate-800 bg-slate-900/40 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Uebersicht</h2>
              <button onClick={() => void load()} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                <RefreshCcw size={12} /> Neu laden
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi icon={ListTodo}   label="Aufgaben"  value={overview.counts.tasks} />
              <Kpi icon={FileText}   label="Berichte"  value={overview.counts.reports} />
              <Kpi icon={FolderTree} label="Dokumente" value={overview.counts.akten_docs} />
              <Kpi icon={LayoutGrid} label="Projekte"  value={overview.counts.projects ?? 0} />
            </div>
            {/* Kurz-Stammdaten aus CustomerSummary (Legacy, read-only) */}
            <div className="mt-4">
              {CustomerSummary
                ? <CustomerSummary customer={overview.customer} />
                : <ModulOffline modul="Stammdaten" />}
            </div>
          </section>
        )}
      </div>

      <ConfirmModal
        open={switchTypusOpen}
        title={contact.typus === 'firma' ? 'Zu Einzelperson umschalten?' : 'Zu Firma umschalten?'}
        message={
          contact.typus === 'firma'
            ? `Dieser Kontakt wird zu einer Einzelperson. Der Firmenname wird geleert; Vor-/Nachname werden nach dem Umschalten pflegbar. Der Kunde bleibt bestehen und behaelt Aufgaben, Berichte und Akten.`
            : `Dieser Kontakt wird zu einer Firma. Vor-/Nachname werden geleert; der Firmenname wird nach dem Umschalten pflegbar. Der Kunde bleibt bestehen und behaelt Aufgaben, Berichte und Akten.${isAnsprechpartner ? ' Die Zuordnung zur bisherigen Firma wird dabei gelöst — der Kontakt wird eine eigenständige Firma.' : ''}`
        }
        confirmLabel="Umschalten"
        busy={switchingTypus}
        onClose={() => setSwitchTypusOpen(false)}
        onConfirm={() => void switchTypus()}
      />
      <UnsavedChangesModal {...session.unsavedModalProps} />
      <SaveFeedback label="Kontakt" error={session.error} clearError={session.clearError} issues={session.issues} clearIssues={session.clearIssues} />
    </div>
  );
}

function FieldOrText({ label, value, editing, onChange, type = 'text', placeholder, className, monospace }: {
  label: string; value: string; editing: boolean;
  onChange: (v: string) => void;
  type?: string; placeholder?: string; className?: string; monospace?: boolean;
}) {
  return (
    <div className={className}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      {editing ? (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
               className={`w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm ${monospace ? 'font-mono' : ''}`} />
      ) : (
        <div className={`text-sm text-slate-100 ${monospace ? 'font-mono' : ''}`}>{value || <span className="text-slate-500 italic">—</span>}</div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value }: {
  icon: React.ComponentType<{ size?: number; className?: string }>; label: string; value: number;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 flex items-center gap-3">
      <Icon size={18} className="text-brand-400 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-lg font-semibold text-foreground leading-none">{value}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
      </div>
    </div>
  );
}
